import { ReactNode, useState, useCallback } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import axios from "axios"
import { captureEvent } from "@/lib/posthog"
import DefaultLayout from "@/layouts/DefaultLayout"
import Container from "@/components/Container"
import { ArrowLeftIcon } from "@heroicons/react/20/solid"
import { EnvelopeIcon } from "@heroicons/react/24/outline"
import { OTPInput } from "input-otp"
import clsx from "clsx"
import Spinner from "@/components/Spinner"
import ErrorMessage from "@/components/ErrorMessage"
import { VerifyProps } from "@/types/types"

const Slot = ({
  char,
  isActive,
  disabled,
}: {
  char: string | null
  isActive: boolean
  disabled: boolean
}) => (
  <div
    className={clsx(
      "flex size-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-xl font-bold shadow-inner sm:size-14",
      {
        "!bg-chessdrop-50 outline-chessdrop-400 outline-[3px] outline-offset-0":
          isActive && !disabled,
      }
    )}
  >
    {char !== null && char}
  </div>
)

const VerifyPage = () => {
  const { email } = usePage<VerifyProps>().props
  const [verifying, setVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  )
  const [resendCooldown, setResendCooldown] = useState(false)

  const handleResend = useCallback(async () => {
    if (resendCooldown) return
    try {
      await axios.post("/api/auth/resend", { email })
      setResendCooldown(true)
      setTimeout(() => setResendCooldown(false), 30_000)
    } catch {
      // silently fail — user can try again
    }
  }, [email, resendCooldown])

  const handleVerify = (otp: string) => {
    router.post(
      "/verify",
      {
        code: otp,
        email,
      },
      {
        onStart: () => setVerifying(true),
        onFinish: () => setVerifying(false),
        onSuccess: () => captureEvent("email_verified"),
        onError: (error) => setVerificationError(error.code),
        async: true,
      }
    )
  }

  return (
    <Container className="flex min-h-[calc(100vh-90px)] flex-1 justify-center pt-12 sm:pt-24">
      <div className="flex w-full max-w-sm flex-col items-center">
        <div className="flex size-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 sm:size-14">
          <EnvelopeIcon className="size-5 text-gray-900 sm:h-6 sm:w-6" />
        </div>
        <h1 className="mt-6 text-center text-2xl font-bold text-gray-900 sm:text-3xl">
          Check your inbox
        </h1>

        <p className="mt-4 text-center text-base text-balance text-gray-600">
          We sent a 6-digit code to
          <br />
          <span className="font-medium text-gray-900">{email}</span>. <br />{" "}
          Please enter it below.
        </p>

        <OTPInput
          maxLength={6}
          autoComplete="one-time-code"
          disabled={verifying}
          pushPasswordManagerStrategy="none"
          data-lpignore="true"
          data-1p-ignore="true"
          containerClassName={clsx(
            "group mt-6 flex justify-center gap-x-1 sm:gap-x-2",
            verifying && "opacity-50"
          )}
          onComplete={(otp) => handleVerify(otp)}
          render={({ slots }) =>
            slots.map((slot, idx) => (
              <Slot key={idx} {...slot} disabled={verifying} />
            ))
          }
        />

        <div className="mt-4 h-6">
          {verifying ? (
            <div className="flex items-center gap-x-3">
              <p className="text-center text-base text-balance text-gray-600">
                Verifying code
              </p>
              <Spinner size="xs" />
            </div>
          ) : (
            <ErrorMessage className="text-center" message={verificationError} />
          )}
        </div>

        <div className="mt-6 flex items-center gap-x-4">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown}
            className={clsx(
              "text-sm font-medium",
              resendCooldown
                ? "cursor-not-allowed text-gray-400"
                : "text-chessdrop-600 hover:text-chessdrop-500"
            )}
          >
            {resendCooldown ? "Code sent" : "Resend code"}
          </button>
          <Link
            href="/login"
            className="flex items-center space-x-1.5 rounded-xl bg-transparent p-2.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <ArrowLeftIcon className="size-5" />
            <span className="text-sm font-medium">Go back</span>
          </Link>
        </div>
      </div>
    </Container>
  )
}

export default VerifyPage

VerifyPage.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>
